import { useState, useCallback, useEffect } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useToast } from "@/components/base/Toast";
import { usePipelineActivities } from "@/components/feature/PipelineActivityContext";
import { KanbanColumn } from "./KanbanColumn";
import { DealCard } from "./DealCard";
import { MoveToStageModal } from "@/pages/leads/detail/components/MoveToStageModal";
import { fetchApi } from "@/services/apiService";
import type { Lead, LeadStage } from "@/mocks/leadsData";
import type { DealMetaMap } from "@/pages/pipeline/page";

export interface StageConfigItem {
  stage: string;
  label: string;
  colorBar: string;
}

const defaultStageConfig: StageConfigItem[] = [
  { stage: "New Inquiry", label: "New Inquiry", colorBar: "bg-gray-400" },
  { stage: "Site Visit Scheduled", label: "Site Visit Scheduled", colorBar: "bg-blue-500" },
  { stage: "Site Visit Done", label: "Site Visit Done", colorBar: "bg-indigo-500" },
  { stage: "Proposal Sent", label: "Proposal Sent", colorBar: "bg-amber-500" },
  { stage: "Negotiation", label: "Negotiation", colorBar: "bg-orange-500" },
  { stage: "Booking Done", label: "Booking Done", colorBar: "bg-green-500" },
  { stage: "Lost", label: "Lost", colorBar: "bg-red-400" },
];

type PipelineData = Record<string, number[]>;

type Props = {
  deals: Lead[];
  pipelineData: PipelineData;
  onPipelineDataChange: (data: PipelineData | ((prev: PipelineData) => PipelineData)) => void;
  dealMetaMap?: DealMetaMap;
  onAddDeal: (stage?: string) => void;
  stageConfig?: StageConfigItem[];
  stageDefinitions?: any[];
};

export function PipelineKanbanBoard({
  deals = [],
  pipelineData,
  onPipelineDataChange,
  dealMetaMap = {},
  onAddDeal,
  stageConfig: propStageConfig,
  stageDefinitions: propStageDefinitions,
}: Props) {
  const { showToast } = useToast();
  const { logStageChange } = usePipelineActivities();
  const [activeDeal, setActiveDeal] = useState<Lead | null>(null);
  const [activeDragOverStage, setActiveDragOverStage] = useState<string | null>(null);
  const [dynamicStages, setDynamicStages] = useState<StageConfigItem[]>([]);
  const [stageDefinitions, setStageDefinitions] = useState<any[]>([]);

  useEffect(() => {
    if (propStageDefinitions && propStageDefinitions.length > 0) {
      setStageDefinitions(propStageDefinitions);
    }
    if (propStageConfig && propStageConfig.length > 0) {
      setDynamicStages(propStageConfig);
      return;
    }

    fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          setStageDefinitions(items);
          setDynamicStages(
            items.map((s: any) => ({
              stage: s.name,
              label: s.name,
              colorBar: s.color || "bg-indigo-500",
            }))
          );
        }
      })
      .catch(() => {});
  }, [propStageConfig, propStageDefinitions]);

  const activeStageConfig =
    dynamicStages.length > 0
      ? dynamicStages
      : propStageConfig && propStageConfig.length > 0
      ? propStageConfig
      : defaultStageConfig;

  const [confirmMove, setConfirmMove] = useState<{
    deal: Lead;
    fromStage: string;
    toStage: string;
    insertIndex: number;
  } | null>(null);

  const [stageChangeConfirm, setStageChangeConfirm] = useState<{
    deal: Lead;
    fromStage: string;
    toStage: string;
    insertIndex: number;
  } | null>(null);

  // Find a lead by ID from dynamic deals list
  const findLead = useCallback(
    (id: number): Lead | null => {
      const direct = deals.find((l) => l.id === id);
      if (direct) return direct;
      const meta = dealMetaMap[id];
      if (!meta) return null;
      const lead = deals.find((l) => l.id === meta.leadId);
      if (!lead) return null;
      return {
        ...lead,
        value: meta.dealValue || lead.value,
        priority: meta.dealPriority || lead.priority,
        assigned: meta.dealAssigned || lead.assigned,
      };
    },
    [deals, dealMetaMap]
  );

  // Find which stage a deal ID belongs to
  const findStageForDeal = useCallback(
    (dealId: number): string | null => {
      for (const [stage, ids] of Object.entries(pipelineData)) {
        if (Array.isArray(ids) && ids.includes(dealId)) return stage;
      }
      const direct = deals.find((l) => l.id === dealId);
      if (direct?.stage) return direct.stage;
      return null;
    },
    [pipelineData, deals]
  );

  // Get deals for a stage in display order
  const getDealsForStage = useCallback(
    (stage: string): Lead[] => {
      const ids = pipelineData[stage] || [];
      const stageDeals = ids
        .map((id) => {
          const direct = deals.find((l) => l.id === id);
          if (direct) return { ...direct, stage: stage as LeadStage };
          const meta = dealMetaMap[id];
          if (!meta) return null;
          const lead = deals.find((l) => l.id === meta.leadId);
          if (!lead) return null;
          return {
            ...lead,
            stage: stage as LeadStage,
            value: meta.dealValue || lead.value,
            priority: meta.dealPriority || lead.priority,
            assigned: meta.dealAssigned || lead.assigned,
          };
        })
        .filter((l): l is Lead => !!l);

      // Also include any leads currently having this stage if pipelineData was empty
      if (stageDeals.length === 0 && (!ids || ids.length === 0)) {
        return deals.filter((l) => l.stage === stage);
      }

      return stageDeals;
    },
    [pipelineData, deals, dealMetaMap]
  );

  // Sensors — mouse + touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const dealId = Number(event.active.id);
      const deal = findLead(dealId);
      setActiveDeal(deal);
    },
    [findLead]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over } = event;
      if (!over) {
        setActiveDragOverStage(null);
        return;
      }

      // Check if over a column directly
      const overStage = activeStageConfig.find((s) => s.stage === over.id);
      if (overStage) {
        setActiveDragOverStage(overStage.stage);
        return;
      }

      // Check if over a card — find its column
      const overStageFromCard = findStageForDeal(Number(over.id));
      if (overStageFromCard) {
        setActiveDragOverStage(overStageFromCard);
      }
    },
    [activeStageConfig, findStageForDeal]
  );

  // Handle actual movement of deal between/within stages
  const executeMove = useCallback(
    async (dealId: number, fromStage: string, toStage: string, insertIndex: number, extraFieldValues?: Record<string, string>) => {
      const deal = findLead(dealId);
      const mergedExtraFields = {
        ...(deal?.extraFields || (deal as any)?.extra_fields || {}),
        ...(extraFieldValues || {}),
      };

      try {
        await fetchApi(`/leads/${dealId}`, {
          method: "PATCH",
          body: JSON.stringify({
            stage: toStage,
            extraFields: mergedExtraFields,
          }),
        });
      } catch (e) {
        console.error("Error updating lead stage in API:", e);
      }

      onPipelineDataChange((prev: PipelineData) => {
        const next: PipelineData = { ...prev };
        // Ensure arrays exist
        if (!next[fromStage]) next[fromStage] = [];
        if (!next[toStage]) next[toStage] = [];

        // Remove from source
        next[fromStage] = next[fromStage].filter((id) => id !== dealId);
        // Insert at target position
        const target = [...next[toStage]];
        target.splice(insertIndex, 0, dealId);
        next[toStage] = target;
        return next;
      });

      if (deal && fromStage !== toStage) {
        showToast(`${deal.name} moved to ${toStage}`, "success");

        const meta = dealMetaMap[dealId];
        const activityLeadId = meta ? meta.leadId : deal.id;

        const extraPairs = Object.entries(extraFieldValues || {}).filter(([, v]) => typeof v === "string" ? v.trim() : (v !== undefined && v !== null && v !== ""));
        let desc = `Stage changed from ${fromStage} to ${toStage} via pipeline board`;
        if (extraPairs.length > 0) {
          desc += `\n${extraPairs.map(([k, v]) => `${k}: ${v}`).join(", ")}`;
        }

        logStageChange({
          leadId: activityLeadId,
          description: desc,
          agent: deal.assigned || "Admin",
          timestamp: "Just now",
        });
      }
    },
    [findLead, showToast, logStageChange, onPipelineDataChange, dealMetaMap]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDeal(null);
      setActiveDragOverStage(null);

      if (!over) return;

      const dealId = Number(active.id);
      const fromStage = findStageForDeal(dealId);
      if (!fromStage) return;

      // Determine target stage and position
      let toStage: string;
      let insertIndex: number;

      const overStageMatch = activeStageConfig.find((s) => s.stage === over.id);
      if (overStageMatch) {
        toStage = overStageMatch.stage;
        if (fromStage === toStage) {
          const currentIds = pipelineData[toStage] || [];
          const currentIdx = currentIds.indexOf(dealId);
          if (currentIdx >= 0) {
            const without = currentIds.filter((id) => id !== dealId);
            insertIndex = without.length;
          } else {
            insertIndex = currentIds.length;
          }
        } else {
          insertIndex = (pipelineData[toStage] || []).length;
        }
      } else {
        const overDealId = Number(over.id);
        toStage = findStageForDeal(overDealId) || fromStage;
        const targetIds = pipelineData[toStage] || [];
        const overIdx = targetIds.indexOf(overDealId);
        insertIndex = overIdx >= 0 ? overIdx + 1 : targetIds.length;

        if (fromStage === toStage && dealId === overDealId) return;
      }

      // If no change, do nothing
      if (fromStage === toStage) {
        const currentIds = pipelineData[toStage] || [];
        const currentIdx = currentIds.indexOf(dealId);
        if (currentIdx === insertIndex || currentIdx + 1 === insertIndex) return;
      }

      // For cross-stage moves, check special stages
      if (fromStage !== toStage) {
        const deal = findLead(dealId);
        if (deal) {
          if (toStage === "Booking Done" || toStage === "Lost" || toStage === "Closed Won" || toStage === "Closed Lost") {
            setConfirmMove({ deal, fromStage, toStage, insertIndex });
            return;
          }
          setStageChangeConfirm({ deal, fromStage, toStage, insertIndex });
          return;
        }
      }

      executeMove(dealId, fromStage, toStage, insertIndex);
    },
    [activeStageConfig, findStageForDeal, pipelineData, findLead, executeMove]
  );

  const handleConfirmMove = useCallback(() => {
    if (!confirmMove) return;
    executeMove(confirmMove.deal.id, confirmMove.fromStage, confirmMove.toStage, confirmMove.insertIndex);
    setConfirmMove(null);
  }, [confirmMove, executeMove]);

  const handleCancelMove = useCallback(() => {
    setConfirmMove(null);
  }, []);

  const handleStageChangeConfirm = useCallback(
    (extraFieldValues: Record<string, string>) => {
      if (!stageChangeConfirm) return;
      executeMove(
        stageChangeConfirm.deal.id,
        stageChangeConfirm.fromStage,
        stageChangeConfirm.toStage,
        stageChangeConfirm.insertIndex,
        extraFieldValues
      );
      setStageChangeConfirm(null);
    },
    [stageChangeConfirm, executeMove]
  );

  const handleStageChangeCancel = useCallback(() => {
    setStageChangeConfirm(null);
  }, []);

  const isOverStage = useCallback(
    (stage: string) => activeDragOverStage === stage,
    [activeDragOverStage]
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {activeStageConfig.map((config) => (
            <SortableContext
              key={config.stage}
              id={config.stage}
              items={(pipelineData[config.stage] || getDealsForStage(config.stage).map((d) => d.id)).map(String)}
              strategy={verticalListSortingStrategy}
            >
              <KanbanColumn
                stage={config.stage}
                label={config.label}
                deals={getDealsForStage(config.stage)}
                colorBar={config.colorBar}
                isDragOver={isOverStage(config.stage)}
                isDealDragging={!!activeDeal}
                onAddDeal={onAddDeal}
              />
            </SortableContext>
          ))}
        </div>

        {/* Drag overlay — floating card */}
        <DragOverlay dropAnimation={null}>
          {activeDeal ? (
            <div className="w-[290px]">
              <DealCard deal={{ ...activeDeal }} isDragOverlay />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Confirmation popover for Booking Done / Lost */}
      {confirmMove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl p-6 max-w-sm w-full mx-4 z-10 animate-scale-in">
            {confirmMove.toStage === "Booking Done" || confirmMove.toStage === "Closed Won" ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-green-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Mark as Won / Booking Done?</h3>
                    <p className="text-xs text-gray-500 mt-0.5">This will update the deal stage to {confirmMove.toStage}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <button
                    type="button"
                    onClick={handleCancelMove}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmMove}
                    className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Confirm Won
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-red-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Mark as Lost?</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      This deal will be marked as {confirmMove.toStage}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <button
                    type="button"
                    onClick={handleCancelMove}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmMove}
                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Mark as Lost
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Stage change with dynamic extra fields modal */}
      {stageChangeConfirm && (
        <MoveToStageModal
          leadName={stageChangeConfirm.deal.name}
          targetStage={stageChangeConfirm.toStage}
          stageDefinitions={stageDefinitions}
          onClose={handleStageChangeCancel}
          onConfirm={handleStageChangeConfirm}
        />
      )}
    </div>
  );
}